/**
 * Ic — Central icon set. Stroke + fill icons for 24px viewBox.
 * Design source: prototype.jsx:444-532 (flat icon set).
 *
 * Usage:
 *   <Ic name="bell" size={20} color={P.ink} />
 *
 * Atom purity: imports only react.
 * Paths sourced from prototype.jsx. Multi-path icons (camera) handled via
 * the `paths` field instead of the single `d` field.
 */
import { CSSProperties } from 'react';

// ─── Icon path definitions ────────────────────────────────────────────────────
// Each icon is either:
//   { kind: 'fill'|'stroke', d: string }       — single path
//   { kind: 'fill'|'stroke', paths: string[] } — multiple paths (e.g. camera body + lens)

type IconDef =
  | { kind: 'stroke' | 'fill'; d: string; paths?: never }
  | { kind: 'stroke' | 'fill'; paths: string[]; d?: never };

const ICONS = {
  // ── Navigation ─────────────────────────────────────────────────────────────
  chevL: {
    kind: 'stroke',
    d: 'M15 19l-7-7 7-7',
  },
  // prototype:515-519 — chevR uses 8×14 viewBox in prototype but here we keep 24×24
  chevR: {
    kind: 'stroke',
    d: 'M9 5l7 7-7 7',
  },
  back: {
    kind: 'stroke',
    d: 'M19 12H5M12 19l-7-7 7-7',
  },
  xmark: {
    kind: 'stroke',
    d: 'M6 6l12 12M18 6L6 18',
  },
  // alias for xmark (prototype:521-524)
  cross: {
    kind: 'stroke',
    d: 'M6 6l12 12M18 6L6 18',
  },

  // ── Notifications ──────────────────────────────────────────────────────────
  // prototype:510-513
  bell: {
    kind: 'fill',
    d: 'M12 22a2.5 2.5 0 002.45-2H9.55A2.5 2.5 0 0012 22zm7-5l-1.6-1.6c-.26-.26-.4-.6-.4-.97V11a5 5 0 00-4-4.9V5a1 1 0 10-2 0v1.1A5 5 0 007 11v3.43c0 .37-.14.71-.4.97L5 17v1h14v-1z',
  },

  // ── Brand / actions ────────────────────────────────────────────────────────
  // prototype:450-453
  flame: {
    kind: 'fill',
    d: 'M12 2c1.5 3 4 4.5 4 8a4 4 0 11-8 0c0-1 .3-1.6.8-2.3-.2 2 .7 2.8 1.7 2.8.5-2.5-1-4 1.5-8.5zm0 20a6 6 0 006-6c0-3-2-5-3-7-.5 3-2 4-3 4s-2-.5-2-2c-2 1.5-4 3-4 6a6 6 0 006 5z',
  },
  // prototype:495-498
  plus: {
    kind: 'stroke',
    d: 'M12 5v14M5 12h14',
  },

  // ── Finance / commerce ─────────────────────────────────────────────────────
  // prototype:455-458
  card: {
    kind: 'fill',
    d: 'M3 7.5A2.5 2.5 0 015.5 5h13A2.5 2.5 0 0121 7.5V9H3V7.5zM3 11h18v5.5a2.5 2.5 0 01-2.5 2.5h-13A2.5 2.5 0 013 16.5V11zm3 4a1 1 0 011-1h3a1 1 0 110 2H7a1 1 0 01-1-1z',
  },
  // prototype:460-463
  tag: {
    kind: 'fill',
    d: 'M3 4.5A1.5 1.5 0 014.5 3h6.7c.4 0 .8.16 1.06.44l8.3 8.3a1.5 1.5 0 010 2.12l-6.7 6.7a1.5 1.5 0 01-2.12 0l-8.3-8.3A1.5 1.5 0 013 11.2V4.5zM7.5 9a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
  },
  // prototype:465-468
  chart: {
    kind: 'fill',
    d: 'M4 20a1 1 0 01-1-1V8a1 1 0 011-1h2a1 1 0 011 1v11a1 1 0 01-1 1H4zm7 0a1 1 0 01-1-1V4a1 1 0 011-1h2a1 1 0 011 1v15a1 1 0 01-1 1h-2zm7 0a1 1 0 01-1-1v-7a1 1 0 011-1h2a1 1 0 011 1v7a1 1 0 01-1 1h-2z',
  },

  // ── Utility ────────────────────────────────────────────────────────────────
  // prototype:470-473
  question: {
    kind: 'fill',
    d: 'M12 22a10 10 0 100-20 10 10 0 000 20zM9.5 9a2.5 2.5 0 015 0c0 .9-.5 1.4-1.4 2-.7.5-1.1.8-1.1 1.5v.5a1 1 0 102 0v-.4c0-.2.2-.4.7-.7 1-.7 1.8-1.5 1.8-2.9a4.5 4.5 0 10-9 0 1 1 0 102 0zM12 17a1.2 1.2 0 100-2.4 1.2 1.2 0 000 2.4z',
  },
  // prototype:475-478
  doc: {
    kind: 'fill',
    d: 'M5 4.5A2.5 2.5 0 017.5 2h6.6c.4 0 .78.16 1.06.44l4.4 4.4c.28.28.44.66.44 1.06V19.5a2.5 2.5 0 01-2.5 2.5h-10A2.5 2.5 0 015 19.5v-15zm9 1V8a1 1 0 001 1h2.5L14 5.5zM7.5 12a1 1 0 011-1h7a1 1 0 110 2h-7a1 1 0 01-1-1zm1 3a1 1 0 100 2h7a1 1 0 100-2h-7z',
  },
  // prototype:480-483
  lock: {
    kind: 'fill',
    d: 'M7 9V7a5 5 0 1110 0v2h.5A2.5 2.5 0 0120 11.5v8a2.5 2.5 0 01-2.5 2.5h-11A2.5 2.5 0 014 19.5v-8A2.5 2.5 0 016.5 9H7zm2 0V7a3 3 0 116 0v2H9z',
  },
  info: {
    kind: 'fill',
    d: 'M12 22a10 10 0 100-20 10 10 0 000 20zm-1-7h2v-4h-2v4zm0-6h2V7h-2v2z',
  },

  // ── Status / flow ──────────────────────────────────────────────────────────
  // prototype:505-508
  check: {
    kind: 'stroke',
    d: 'M5 12.5l4.5 4.5L19 7',
  },
  // prototype:445-448 (fillRule evenodd)
  clock: {
    kind: 'fill',
    d: 'M12 22a10 10 0 100-20 10 10 0 000 20zm.75-15.5a.75.75 0 00-1.5 0v5.75c0 .2.08.39.22.53l3.5 3.5a.75.75 0 101.06-1.06l-3.28-3.28V6.5z',
  },
  // prototype:485-489
  warning: {
    kind: 'fill',
    d: 'M10.7 2.7a1.5 1.5 0 012.6 0l9 16a1.5 1.5 0 01-1.3 2.3H3a1.5 1.5 0 01-1.3-2.3l9-16zM12 9a1 1 0 011 1v4a1 1 0 11-2 0v-4a1 1 0 011-1zm0 9a1.2 1.2 0 100-2.4 1.2 1.2 0 000 2.4z',
  },
  // prototype:526-530 — camera has 2 strokes: body + lens circle
  camera: {
    kind: 'stroke',
    paths: [
      'M4 8.5A1.5 1.5 0 015.5 7h2.7l1.2-1.8A1 1 0 0110.25 5h3.5a1 1 0 01.85.45L15.8 7h2.7A1.5 1.5 0 0120 8.5v9A1.5 1.5 0 0118.5 19h-13A1.5 1.5 0 014 17.5v-9z',
      'M12 10a3 3 0 110 6 3 3 0 010-6z',
    ],
  },
  // prototype:490-493
  arrowUR: {
    kind: 'stroke',
    d: 'M7 17L17 7M17 7H9M17 7v8',
  },

  // ── Tab bar icons (FTR-998) ────────────────────────────────────────────────
  // Single-path stroke glyphs, 24×24 viewBox. Sourced from prototype.jsx:589-622.
  // Names checked against existing keys — no collision (card=credit-card, tasksTab suffixed).
  home: {
    kind: 'stroke',
    d: 'M3 11l9-7 9 7v9a2 2 0 01-2 2h-4v-6h-6v6H5a2 2 0 01-2-2v-9z',
  },
  tasksTab: {
    kind: 'stroke',
    d: 'M5 4.5h14a1.5 1.5 0 011.5 1.5v13a1.5 1.5 0 01-1.5 1.5H5A1.5 1.5 0 013.5 19V6A1.5 1.5 0 015 4.5zM8 11l3 3 5-6',
  },
  wallet: {
    kind: 'stroke',
    d: 'M4 7.5A1.5 1.5 0 015.5 6h13A1.5 1.5 0 0120 7.5V18a1.5 1.5 0 01-1.5 1.5h-13A1.5 1.5 0 014 18V7.5zM4 10h16M16 14h2',
  },
  profile: {
    kind: 'stroke',
    d: 'M12 5a3.5 3.5 0 110 7 3.5 3.5 0 010-7zM5 20c0-3.5 3.2-6 7-6s7 2.5 7 6',
  },
} as const satisfies Record<string, IconDef>;

export type IconName = keyof typeof ICONS;

export interface IcProps {
  name: IconName;
  size?: number;
  color?: string;
  style?: CSSProperties;
}

export function Ic({ name, size = 20, color = 'currentColor', style }: IcProps) {
  const icon = ICONS[name];
  const isStroke = icon.kind === 'stroke';

  const svgProps = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: isStroke ? 'none' : color,
    stroke: isStroke ? color : undefined,
    strokeWidth: isStroke ? (2 as number | undefined) : undefined,
    strokeLinecap: isStroke ? ('round' as const) : undefined,
    strokeLinejoin: isStroke ? ('round' as const) : undefined,
    style: { flexShrink: 0, ...style } as CSSProperties,
    'aria-hidden': true as const,
  };

  // Multi-path icons (e.g. camera: body + lens circle)
  if ('paths' in icon) {
    return (
      <svg {...svgProps}>
        {icon.paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
    );
  }

  const singlePath = icon as { kind: 'stroke' | 'fill'; d: string };
  return (
    <svg {...svgProps}>
      <path d={singlePath.d} />
    </svg>
  );
}
